import {
  Box,
  Typography,
  Paper,
  Switch,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAdminFeatures, updateAdminFeature } from "../../api/admin";

export default function AdminFeaturesPage() {
  const qc = useQueryClient();

  const { data: features = [], isLoading, isError } = useQuery({
    queryKey: ["admin", "features"],
    queryFn: fetchAdminFeatures,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ featureId, is_active }: { featureId: string; is_active: boolean }) =>
      updateAdminFeature(featureId, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "features"] }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
        <CircularProgress size={28} sx={{ color: "#C9734F" }} />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load features.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <TuneRoundedIcon sx={{ color: "#C9734F", fontSize: 22 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A" }}>
          Features
        </Typography>
        <Chip label={`${features.length} total`} size="small" sx={{ ml: 1, backgroundColor: "#F1F5F9", color: "#64748B" }} />
      </Box>

      <Typography sx={{ color: "#64748B", fontSize: "0.85rem", mb: 3 }}>
        Toggle features active/inactive. Inactive features are denied regardless of plan or overrides.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {features.map((feat) => (
          <Paper
            key={feat.feature_id}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              display: "flex",
              alignItems: "center",
              gap: 2,
              opacity: feat.is_active ? 1 : 0.6,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#0F172A" }}>
                  {feat.display_name}
                </Typography>
                <Chip
                  label={feat.feature_type}
                  size="small"
                  sx={{
                    backgroundColor: feat.feature_type === "metered" ? "#EFF6FF" : "#F0FDF4",
                    color: feat.feature_type === "metered" ? "#3B82F6" : "#16A34A",
                    fontSize: "0.68rem",
                    height: 18,
                    fontWeight: 600,
                  }}
                />
                {feat.storage_unit && (
                  <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                    unit: {feat.storage_unit}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: "0.78rem", color: "#94A3B8", fontFamily: "monospace" }}>
                {feat.feature_key}
              </Typography>
              {feat.description && (
                <Typography sx={{ fontSize: "0.8rem", color: "#64748B", mt: 0.5 }}>
                  {feat.description}
                </Typography>
              )}
            </Box>

            <Switch
              checked={feat.is_active}
              size="small"
              disabled={toggleMutation.isPending}
              onChange={(_, checked) =>
                toggleMutation.mutate({ featureId: feat.feature_id, is_active: checked })
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#C9734F" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#C9734F" },
              }}
            />
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
